// src/components/Responsabile/ResponsabileGalleriaCantiere.jsx
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs 
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

// Import CSS unificato (stesso degli admin)
import '../Admin/Cantieri/GalleriaCantiere.css';

const ResponsabileGalleriaCantiere = ({ cantiere, onBack }) => {
  const { currentUser } = useAuth();
  
  const [allegati, setAllegati] = useState([]);
  const [filteredAllegati, setFilteredAllegati] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Filtri
  const [filters, setFilters] = useState({
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
    fileType: 'all'
  });
  
  // Caricamento dati
  useEffect(() => {
    loadAllegati();
  }, [cantiere.id]);
  
  // Applicazione filtri
  useEffect(() => {
    applyFilters();
  }, [allegati, filters]);
  
  const loadAllegati = async () => {
    try {
      setIsLoading(true);
      
      // Carica tutti i rapportini del cantiere
      const rapportiniRef = collection(db, 'rapportini');
      const q = query(
        rapportiniRef,
        where('cantiereId', '==', cantiere.id),
        orderBy('data', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const rapportini = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Estrai tutti gli allegati dai rapportini
      const tuttiAllegati = [];
      rapportini.forEach(rapportino => {
        if (rapportino.allegati && Array.isArray(rapportino.allegati)) {
          rapportino.allegati.forEach(allegato => {
            tuttiAllegati.push({
              ...allegato,
              rapportinoId: rapportino.id,
              rapportinoData: rapportino.data,
              rapportinoRedattore: rapportino.redattoDa
            });
          });
        }
      });
      
      // Ordina per data più recente
      const allegatiOrdinati = tuttiAllegati.sort((a, b) => {
        const dateA = new Date(a.uploadedAt || a.rapportinoData);
        const dateB = new Date(b.uploadedAt || b.rapportinoData);
        return dateB - dateA;
      });
      
      setAllegati(allegatiOrdinati);
      console.log(`Caricati ${allegatiOrdinati.length} allegati`);
      
    } catch (error) {
      console.error('Errore nel caricamento allegati:', error);
      showNotification('Errore nel caricamento della galleria', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const applyFilters = () => {
    let filtered = [...allegati];
    
    // Filtro per nome file
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(allegato =>
        allegato.nome.toLowerCase().includes(searchLower) ||
        allegato.nomeFile.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtro per data
    if (filters.dateFrom) {
      filtered = filtered.filter(allegato => {
        const allegatoDate = new Date(allegato.uploadedAt || allegato.rapportinoData);
        return allegatoDate >= new Date(filters.dateFrom);
      });
    }
    
    if (filters.dateTo) {
      filtered = filtered.filter(allegato => {
        const allegatoDate = new Date(allegato.uploadedAt || allegato.rapportinoData);
        return allegatoDate <= new Date(filters.dateTo);
      });
    }
    
    // Filtro per tipo file
    if (filters.fileType !== 'all') {
      if (filters.fileType === 'images') {
        filtered = filtered.filter(allegato => 
          allegato.tipo && allegato.tipo.startsWith('image/')
        );
      } else if (filters.fileType === 'documents') {
        filtered = filtered.filter(allegato => 
          allegato.tipo && !allegato.tipo.startsWith('image/')
        );
      }
    }
    
    setFilteredAllegati(filtered);
  };
  
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };
  
  const formatDate = (date) => {
    if (!date) return 'Data non disponibile';
    return new Date(date).toLocaleDateString('it-IT');
  };
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const getFileIcon = (tipo) => {
    if (tipo?.startsWith('image/')) return '🖼️';
    if (tipo?.includes('pdf')) return '📄';
    return '📎';
  };
  
  const handleImageClick = (allegato) => {
    if (allegato.tipo && allegato.tipo.startsWith('image/')) {
      setSelectedImage(allegato);
    } else {
      // Apri il file in una nuova finestra
      window.open(allegato.url, '_blank');
    }
  };
  
  const closeModal = () => {
    setSelectedImage(null);
  };
  
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Caricamento galleria...</p>
      </div>
    );
  }
  
  return (
    <div className="galleria-cantiere">
      {/* Notifiche */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      {/* Header */}
      <div className="galleria-header">
        <div className="header-left">
          <button className="btn-back" onClick={onBack}>
            ← Torna al Cantiere
          </button>
          <div className="header-title">
            <h2>🖼️ Galleria - {cantiere.nomeCommessa}</h2>
            <p>{filteredAllegati.length} di {allegati.length} file</p>
          </div>
        </div>
      </div>
      
      {/* Filtri */}
      <div className="galleria-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Cerca per nome file..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
              className="filter-input"
            />
          </div>
          
          <div className="filter-group">
            <label>Da:</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              className="filter-input"
            />
          </div>
          
          <div className="filter-group">
            <label>A:</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              className="filter-input"
            />
          </div>
          
          <div className="filter-group">
            <select
              value={filters.fileType}
              onChange={(e) => setFilters({...filters, fileType: e.target.value})}
              className="filter-select"
            >
              <option value="all">Tutti i file</option>
              <option value="images">Solo immagini</option>
              <option value="documents">Solo documenti</option>
            </select>
          </div>
          
          <div className="filter-group">
            <button 
              onClick={() => setFilters({
                searchTerm: '',
                dateFrom: '',
                dateTo: '',
                fileType: 'all'
              })}
              className="btn btn-secondary"
            >
              Pulisci Filtri
            </button>
          </div>
        </div>
      </div>
      
      {/* Griglia Allegati */}
      {filteredAllegati.length > 0 ? (
        <div className="allegati-grid">
          {filteredAllegati.map((allegato, index) => (
            <div 
              key={index} 
              className="allegato-card"
              onClick={() => handleImageClick(allegato)}
            >
              <div className="allegato-preview">
                {allegato.tipo && allegato.tipo.startsWith('image/') ? (
                  <img 
                    src={allegato.url} 
                    alt={allegato.nome}
                    className="allegato-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-image.png';
                    }}
                  />
                ) : (
                  <div className="allegato-file-icon">
                    {getFileIcon(allegato.tipo)}
                  </div>
                )}
                <div className="allegato-overlay">
                  <button className="btn-view">
                    {allegato.tipo && allegato.tipo.startsWith('image/') ? '🔍' : '📎'}
                  </button>
                </div>
              </div>
              
              <div className="allegato-info">
                <h4 className="allegato-nome" title={allegato.nome}>
                  {allegato.nome}
                </h4>
                <div className="allegato-meta">
                  <span className="allegato-size">
                    {formatFileSize(allegato.dimensione)}
                  </span>
                  <span className="allegato-date">
                    {formatDate(allegato.uploadedAt || allegato.rapportinoData)}
                  </span>
                </div>
                <div className="allegato-rapportino">
                  Da rapportino del {formatDate(allegato.rapportinoData)}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">🖼️</div>
          <h3>Nessun file trovato</h3>
          <p>
            {allegati.length === 0 ? 
              'Non ci sono ancora allegati in questo cantiere. Aggiungi file ai rapportini per popolare la galleria.' :
              'Nessun file corrisponde ai filtri selezionati. Prova a modificare i criteri di ricerca.'
            }
          </p>
        </div>
      )}
      
      {/* Modal Visualizzazione Immagine */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={closeModal}>
          <div className="image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <h3>{selectedImage.nome}</h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            
            <div className="image-modal-content">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.nome}
                className="modal-image"
              />
            </div>
            
            <div className="image-modal-footer">
              <div className="image-info">
                <span>Dimensione: {formatFileSize(selectedImage.dimensione)}</span>
                <span>Data: {formatDate(selectedImage.uploadedAt)}</span>
                <span>Rapportino: {formatDate(selectedImage.rapportinoData)}</span>
              </div>
              <div className="image-actions">
                <a 
                  href={selectedImage.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Apri in nuova finestra
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponsabileGalleriaCantiere;